import { useState, useEffect, useCallback } from 'react';
import {
    digitalProductService,
    DigitalProduct,
    DigitalProductPricing
} from '@/services/digitalProduct.service';

/**
 * Hook to fetch a single digital product by slug
 */
export function useDigitalProduct(slug: string | undefined) {
    const [product, setProduct] = useState<DigitalProduct | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchProduct = useCallback(async () => {
        if (!slug) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);
            const response = await digitalProductService.getProductBySlug(slug);

            if (response.success && response.data) {
                setProduct(response.data);
            } else {
                setError(response.error || 'Failed to fetch product');
            }
        } catch (err) {
            setError('Failed to fetch product');
        } finally {
            setLoading(false);
        }
    }, [slug]);

    useEffect(() => {
        fetchProduct();
    }, [fetchProduct]);

    return {
        product,
        loading,
        error,
        refetch: fetchProduct,
    };
}

/**
 * Hook to fetch digital product pricing
 */
export function useDigitalProductPricing(slug: string | undefined) {
    const [pricing, setPricing] = useState<DigitalProductPricing | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchPricing = useCallback(async () => {
        if (!slug) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);
            const response = await digitalProductService.getProductPricing(slug);

            if (response.success && response.data) {
                setPricing(response.data);
            } else {
                setError(response.error || 'Failed to fetch pricing');
            }
        } catch (err) {
            setError('Failed to fetch pricing');
        } finally {
            setLoading(false);
        }
    }, [slug]);

    useEffect(() => {
        fetchPricing();
    }, [fetchPricing]);

    return {
        pricing,
        loading,
        error,
        refetch: fetchPricing,
    };
}

export default useDigitalProduct;
